import { useEffect, useState } from "react";
import { useSim } from "@/context/SimContext";
import { api } from "@/lib/api";
import { Card, SectionTitle, StatusDot, catColor, LearnLink } from "@/components/ui-kit";
import { Camera, Cpu, Crosshair, MapPin } from "lucide-react";

const ARM_IMG = "https://static.prod-images.emergentagent.com/jobs/92690209-b493-4230-bc74-6cd11c8ee74a/images/c4ebb946d977ab9500016def975ef9d351c9ec718f6a684ee469cc0d2e987966.jpeg";

export default function LiveControl() {
  const { state, eventTick } = useSim();
  const [events, setEvents] = useState([]);
  const lv = state?.live_view;
  const pending = state?.pending_decision;
  const task = state?.current_task;

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/events?limit=8");
        if (active) setEvents(data);
      } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => { active = false; clearInterval(t); };
  }, [eventTick]);

  const conf = lv?.confidence ?? 0;
  const lowConf = pending && conf < (pending?.threshold ?? 75);

  return (
    <div className="space-y-6 ngx-fade-in">
      <div className="flex items-center justify-between">
        <SectionTitle sub="Real-time view of the simulated industrial cell.">Live Control</SectionTitle>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-emerald-950/40 border border-emerald-800 text-emerald-300 text-xs font-mono uppercase tracking-widest">
          <StatusDot tone="success" /> System Online · Task Running
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Live camera / system view */}
        <Card className="col-span-12 lg:col-span-8 p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 h-11 border-b border-slate-800">
            <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
              <Camera className="h-4 w-4 text-blue-400" /> Live System View
            </div>
            <div className="flex items-center gap-3">
              <LearnLink to="/academy/lesson/ai-confidence" label="Learn: AI Confidence" testid="learn-ai-confidence" />
              <span className="font-mono text-xs text-slate-500">CAM-01 · Simulation Cell 01</span>
            </div>
          </div>
          <div className="relative aspect-video bg-black">
            <img src={ARM_IMG} alt="live system" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ngx-grid-overlay opacity-25" />
            <div className="ngx-scanline" />
            {/* bounding box */}
            {lv?.bbox && (
              <div
                className={`absolute border-2 rounded-sm ${lowConf ? "border-amber-400" : "border-emerald-400"}`}
                style={{ left: `${lv.bbox.x}%`, top: `${lv.bbox.y}%`, width: `${lv.bbox.w}%`, height: `${lv.bbox.h}%` }}
              >
                <span className={`absolute -top-6 left-0 text-[11px] font-mono px-1.5 py-0.5 rounded-sm text-black ${lowConf ? "bg-amber-400" : "bg-emerald-400"}`}>
                  {lv.detected_object} · {conf}%
                </span>
              </div>
            )}
            {/* HUD top-left */}
            <div className="absolute top-3 left-3 flex items-center gap-2 text-emerald-400 text-xs font-mono">
              <StatusDot tone="success" /> VISION FEED · LIVE
            </div>
            <div className="absolute top-3 right-3 text-xs font-mono text-slate-300 bg-black/50 px-2 py-1 rounded-sm">
              TASK {lv?.task_id || "—"}
            </div>
            {/* action sequence */}
            <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 flex-wrap">
              {(lv?.action_sequence || []).map((a) => (
                <span key={a}
                  className={`text-[11px] font-mono px-2 py-1 rounded-sm border ${
                    a === lv?.action ? "bg-blue-600/80 border-blue-400 text-white" : "bg-black/50 border-slate-600 text-slate-300"
                  }`}>
                  {a}
                </span>
              ))}
            </div>
          </div>
          {/* readouts */}
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-800 border-t border-slate-800 font-mono">
            <Readout label="Detected" value={lv?.detected_object || "—"} />
            <Readout label="Classification" value={lv?.classification || "—"} />
            <Readout label="Confidence" value={`${conf}%`} tone={lowConf ? "text-amber-400" : "text-emerald-400"} />
            <Readout label="Position" value={lv ? `${lv.position.x}, ${lv.position.y}` : "—"} />
          </div>
        </Card>

        {/* Side status + task */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3 text-slate-300">
              <Cpu className="h-4 w-4 text-blue-400" />
              <h2 className="font-display text-base font-semibold">Current Task</h2>
            </div>
            <div className="font-mono text-2xl text-slate-100">#{task ? String(task.task_id).padStart(4, "0") : "—"}</div>
            <div className="text-sm text-slate-400 mb-3">{task?.name}</div>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="uppercase tracking-wider text-slate-500">Progress</span>
              <span className="font-mono text-slate-300">{task?.progress ?? 0}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${task?.progress ?? 0}%` }} />
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-slate-400">Status</span>
              <span className={`font-mono ${task?.status === "RUNNING" ? "text-emerald-400" : "text-amber-400"}`}>{task?.status}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-slate-400">Human Interventions</span>
              <span className="font-mono text-slate-100">{task?.interventions ?? 0}</span>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="px-4 h-10 flex items-center border-b border-slate-800 text-slate-300 text-sm font-medium gap-2">
              <Crosshair className="h-4 w-4 text-blue-400" /> Event Log (Live)
            </div>
            <div className="divide-y divide-slate-800/60 max-h-64 overflow-y-auto" data-testid="live-event-log">
              {events.map((e) => (
                <div key={e.id} className="px-4 py-2 flex items-start gap-2 text-xs font-mono">
                  <span className="text-slate-500 shrink-0">{e.time}</span>
                  <span className={`shrink-0 w-16 ${catColor(e.category)}`}>{e.category}</span>
                  <span className="text-slate-300 truncate">{e.message}</span>
                </div>
              ))}
              {events.length === 0 && <div className="px-4 py-6 text-center text-slate-500 text-sm">No events yet.</div>}
            </div>
          </Card>
        </div>
      </div>

      {pending && (
        <Card className="p-4 border-amber-700/60 bg-amber-950/20 flex items-center gap-3" data-testid="live-pending-hint">
          <MapPin className="h-4 w-4 text-amber-400" />
          <span className="text-sm text-amber-200">
            Human decision required for {pending.event_no}. The decision panel is displayed automatically.
          </span>
        </Card>
      )}
    </div>
  );
}

function Readout({ label, value, tone = "text-slate-100" }) {
  return (
    <div className="p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className={`text-sm ${tone}`}>{value}</div>
    </div>
  );
}
